/* eslint-disable radix */
import React from 'react';
import {
    ADOBE_TOPICS, SgSessionStorageService, SgSubmissionUtil
} from 'saga-modules';
import { SgCustomerPreferenceService } from 'sg-capability-gdpr';
import CommonConstants from 'sg-common-constants';
import { events } from 'gw-platform-events-js';
import _ from 'lodash';
import { ModalNextProvider } from '@jutro/components';
import SgQnbLobSpecificUtil from 'sg-qnb-lob-specific-util';
import ModalCtrl from 'sg-modal-ctrl';
import { publish } from '@jutro/events';

function setSessionStorageData(key, val) {
    if (typeof val !== 'string') {
        // eslint-disable-next-line no-param-reassign
        val = JSON.stringify(val);
    }
    return SgSessionStorageService.setItem(SgSessionStorageService.KEY[key], val);
}

function getSessionStorageData(key) {
    const val = SgSessionStorageService.getItem(SgSessionStorageService.KEY[key]);
    if (val !== null && typeof val === 'string') {
        return JSON.parse(val);
    }
    return val;
}

// ** Returns policyholder from submission */
const getPolicyHolderVal = (modelVM) => {
    return SgSubmissionUtil.getAccountHolder(modelVM).value;
};

// ** Returns address from submission */
const getAddressVal = (modelVM) => {
    return modelVM.baseData.policyAddress.value;
};

function toSafeString(value) {
    // check for undefined as calling toString on undefined will throw exception
    if (value === undefined) return undefined;

    return value.toString();
}

function safeNumberToString(value) {
    if (value === undefined) return undefined;

    const result = value.toString();
    return result.length > 1 && result.startsWith('0')
        ? result.substring(1)
        : result;
}

function isValid(value) {
    return !!value;
}

function sortGroupAreaValues(a, b) {
    if (a.name > b.name) {
        return 1;
    } if (a.name < b.name) {
        return -1;
    }
    return 0;
}

const trackCustomerPermissions = (askSagaConsent, data) => {
    publish(ADOBE_TOPICS.CUSTOMER_PERMISSIONS, {
        trackingData: data,
        askSagaConsent: askSagaConsent
    });
};

function customOrderGroupAreaValues(availableValues) {
    const insurance = availableValues.filter((e) => e.code === CommonConstants.CUSTOMER_PERMISSION_SECTION.INSURANCE_ID);
    if (SgQnbLobSpecificUtil.showAgeasWording()) {
        insurance[0].name = CommonConstants.CUSTOMER_PERMISSION_SECTION.INSURANCE_NAME;
    }
    const otherAvailableValues = availableValues.filter((e) => e.code !== CommonConstants.CUSTOMER_PERMISSION_SECTION.INSURANCE_ID);
    return insurance.concat(otherAvailableValues.sort(sortGroupAreaValues));
}

const modalProps = {
    title: CommonConstants.COMM_PREFERENCE_ERROR_MODAL.TITLE,
    modalJsx: `${CommonConstants.COMM_PREFERENCE_ERROR_MODAL.MESSAGE} ${SgQnbLobSpecificUtil.getCpcProductPhoneNumber()}`,
    closeBtnLabel: CommonConstants.COMM_PREFERENCE_ERROR_MODAL.OK,
    titleVisibility: true,
    className: 'sgInfoModal'
};

// ** delete gdprData session storage  */
const deleteGdprData = () => {
    const gdprRequestData = getSessionStorageData('CPC_REQUEST_DATA');
    const gdprResponseData = getSessionStorageData('CPC_RESPONSE_DATA');
    if (gdprRequestData) {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CPC_REQUEST_DATA);
    }
    if (gdprResponseData) {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CPC_RESPONSE_DATA);
    }
};

// ** create object to store cpc req-data  */
function buildCpcReqData(modelVM) {
    const address = getAddressVal(modelVM);
    const policyHolder = getPolicyHolderVal(modelVM);
    const buildData = {
        title: policyHolder.prefix,
        gender: policyHolder.gender,
        firstName: policyHolder.firstName,
        surname: policyHolder.lastName,
        dateOfBirth: {
            day: policyHolder.dateOfBirth.day,
            month: policyHolder.dateOfBirth.month,
            monthViewValue: policyHolder.dateOfBirth.monthViewValue,
            year: policyHolder.dateOfBirth.year
        },
        houseName: address.addressLine1 === undefined ? '' : address.addressLine1,
        houseNumber: address.addressLine2,
        street: address.addressLine3,
        city: address.city,
        postcode: address.postalCode,

        emailAddress: policyHolder.emailAddress1,
        returnLevelTwo: true,
        sourceSystem: SgQnbLobSpecificUtil.getCpcSourceSystem(modelVM),
        product: SgQnbLobSpecificUtil.getCpcProductType()
    };
    return buildData;
}

// * Construct request and call GDPR to get permissions. If the request is
//  * successful, stores the data (both req and response) in a session storage

function callGdprService(modelVM, callback) {
    const reqData = buildCpcReqData(modelVM);
    const refDataInSessionStorage = getSessionStorageData('CPC_REF_DATA');
    SgCustomerPreferenceService.getCustomerPermissionDetails(reqData)
        .then(async (responseData) => {
            if (responseData) {
                SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CPC_SAVE_CONSENT);
                if (_.isNull(refDataInSessionStorage)) {
                    const refData = await SgCustomerPreferenceService.getCpcOnlineRefData();
                    setSessionStorageData('CPC_REF_DATA', refData);
                }
                setSessionStorageData('CPC_REQUEST_DATA', reqData);
                setSessionStorageData('CPC_RESPONSE_DATA', responseData);
                setSessionStorageData('ASK_SAGA_CONSENT', responseData.askSagaConsent);
            } else {
                deleteGdprData();
            }
            // eslint-disable-next-line no-unused-expressions
            callback && callback();
        }).catch(() => {
            deleteGdprData();
        });
}

// ** Checks if all required Gdpr request fields are complete  */
const isGdprFieldsComplete = (modelVM) => {
    const policyHolderAddress = getAddressVal(modelVM);
    const policyHolder = getPolicyHolderVal(modelVM);

    if (isValid(policyHolder.dateOfBirth)
        && isValid(policyHolder.dateOfBirth.day)
        && isValid(policyHolder.dateOfBirth.monthViewValue)
        && isValid(policyHolder.dateOfBirth.year)) {
        return isValid(policyHolder.prefix)
            && isValid(policyHolder.gender)
            && isValid(policyHolder.firstName)
            && isValid(policyHolder.lastName)
            && isValid(policyHolder.emailAddress1)
            && (isValid(policyHolderAddress.addressLine1) || isValid(policyHolderAddress.addressLine2))
            && isValid(policyHolderAddress.postalCode);
    }
    return false;
};

// ** Compares the data  in session storage with the latest request data from model  */
const compareRequestDataWithSessionStorage = (modelVM) => {
    const cpcData = getSessionStorageData('CPC_REQUEST_DATA');
    const dataToCompare = buildCpcReqData(modelVM);
    if (cpcData) {
        return toSafeString(cpcData.title) === toSafeString(dataToCompare.title)
            && toSafeString(cpcData.gender) === toSafeString(dataToCompare.gender)
            && toSafeString(cpcData.firstName) === toSafeString(dataToCompare.firstName)
            && toSafeString(cpcData.surname) === toSafeString(dataToCompare.surname)
            && toSafeString(cpcData.emailAddress) === toSafeString(dataToCompare.emailAddress)
            && safeNumberToString(parseInt(cpcData.dateOfBirth.day))
            === safeNumberToString(parseInt(dataToCompare.dateOfBirth.day))
            && safeNumberToString(parseInt(cpcData.dateOfBirth.monthViewValue))
            === safeNumberToString(parseInt(dataToCompare.dateOfBirth.monthViewValue))
            && safeNumberToString(parseInt(cpcData.dateOfBirth.year))
            === safeNumberToString(parseInt(dataToCompare.dateOfBirth.year))
            && toSafeString(cpcData.houseName) === toSafeString(dataToCompare.houseName)
            && toSafeString(cpcData.houseNumber) === toSafeString(dataToCompare.houseNumber)
            && toSafeString(cpcData.street) === toSafeString(dataToCompare.street)
            && toSafeString(cpcData.postcode) === toSafeString(dataToCompare.postcode);
    }
    return false;
};

function buildCpcSaveResponseData(modelVM, data) {
    return {
        pKey: data.pKey,
        capturedConsents: data.capturedConsents,
        journey: SgQnbLobSpecificUtil.getCpcJourneyString(modelVM),
        lastUpdatedAgentName: CommonConstants.CUSTOMER_PERMISSION_SECTION.SOURCE.GENERAL,
        wordingID: data.wordingID
    };
}

const getCpcData = (modelVM) => {
    if (isGdprFieldsComplete(modelVM)) {
        if (!compareRequestDataWithSessionStorage(modelVM)) {
            callGdprService(modelVM, () => {
                events.emit(CommonConstants.GDPR_SERVICE_CALLED_EVENT);
            });
        }
    }
};

// Saves the customer preferences. If the saving is successful, updates the session storage with the response and shows error modal
async function submitCpcData(modelVM, saveData) {
    return new Promise((resolve) => {
        SgCustomerPreferenceService.saveCustomerPermissionDetails(buildCpcSaveResponseData(modelVM, saveData)).then((response) => {
            SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CPC_SAVE_CONSENT);
            SgSessionStorageService.removeItem(SgSessionStorageService.KEY.ASK_SAGA_CONSENT);
            // continue if its success
            if (response.dataSavedSuccessfully === true) {
                resolve();
            } else {
                ModalNextProvider.showModal(
                    <ModalCtrl {...modalProps} />
                ).then(() => {
                    resolve();
                });
            }
        }).catch(() => {
            ModalNextProvider.showModal(
                <ModalCtrl {...modalProps} />
            ).then(() => {
                resolve();
            });
        });
    });
}

const saveAggsCpcData = (modelVM) => {
    const saveData = getSessionStorageData('CPC_SAVE_CONSENT');
    return SgCustomerPreferenceService.saveCustomerPermissionDetails(buildCpcSaveResponseData(modelVM, saveData))
        .then((response) => {
            SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CPC_SAVE_CONSENT);
            SgSessionStorageService.removeItem(SgSessionStorageService.KEY.ASK_SAGA_CONSENT);
            return response.dataSavedSuccessfully === true;
        }).catch(() => {
            return false;
        });
};

const saveCpcData = async (modelVM) => {
    const consentDataInSessionStorage = getSessionStorageData('CPC_SAVE_CONSENT');
    if (!_.isNil(consentDataInSessionStorage)) {
        await submitCpcData(modelVM, consentDataInSessionStorage);
    }
};

const shouldCallCpcService = (modelVM) => {
    const cpcSaveData = getSessionStorageData('CPC_SAVE_CONSENT');
    return cpcSaveData && SgSubmissionUtil.isAggregatorQuote(modelVM)
        && getSessionStorageData('ASK_SAGA_CONSENT');
};

export default {
    getCpcData,
    getSessionStorageData,
    setSessionStorageData,
    saveCpcData,
    buildCpcReqData,
    customOrderGroupAreaValues,
    saveAggsCpcData,
    trackCustomerPermissions,
    shouldCallCpcService
};
